import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { qk } from '../queries/keys';
import { fetchTags, type TagChoice, type TagWithCount } from '../queries/tags';
import { PlusIcon } from './icons';
import { TagScopeIcon, TagScopeLabel } from './TagScope';
import { cx } from './ui';

/**
 * The tag combobox: type, see what matches, pick one, or make the word you
 * typed into a new tag. The list is this person's own tags and their
 * team's; a team tag is offered for making only where the recording is the
 * team's and the team lets this person make one, since the API would refuse
 * it anywhere else.
 *
 * Keyboard: arrows move, Enter picks, Escape closes. What Enter picks with
 * nothing highlighted is the exact match if there is one, otherwise the
 * first row, which is the closest match or the offer to make the word.
 *
 * The list is portalled to the body and placed against the input, because
 * the side panel it lives in scrolls and would clip it. It opens downward,
 * or upward when the viewport has more room above.
 */

const LIST_MAX = 256;

type Row =
  | { kind: 'tag'; tag: TagWithCount }
  | { kind: 'create'; name: string; scope: 'personal' | 'team'; teamName: string | null };

export function TagPicker({
  exclude,
  canCreateTeam,
  onPick,
  placeholder = 'Add a tag',
  autoFocus = false,
  disabled = false,
  create = true,
}: {
  /** Tags already applied, left out of the list. */
  exclude: string[];
  /** The recording is the team's, so a team tag may be made for it. */
  canCreateTeam: boolean;
  onPick: (choice: TagChoice, row: { name: string; scope: 'personal' | 'team'; created: boolean }) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Offer to make a new tag from what was typed. Off for a filter. */
  create?: boolean;
}) {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = useId();

  useEffect(() => {
    const handle = window.setTimeout(() => setTerm(text.trim()), 150);
    return () => window.clearTimeout(handle);
  }, [text]);

  const query = useQuery({ queryKey: qk.tags(term), queryFn: () => fetchTags(term), staleTime: 15_000, enabled: open });
  const team = query.data?.team ?? null;
  const matches = (query.data?.tags ?? []).filter((tag) => !exclude.includes(tag.id));
  const typed = text.trim();
  const exact = matches.find((tag) => tag.name.toLowerCase() === typed.toLowerCase());

  const rows: Row[] = matches.map((tag) => ({ kind: 'tag', tag }));
  if (create && typed && !exact && typed.length <= 40) {
    rows.push({ kind: 'create', name: typed, scope: 'personal', teamName: null });
    if (team && canCreateTeam && team.canCreate !== false) rows.push({ kind: 'create', name: typed, scope: 'team', teamName: team.name });
  }

  const showing = open && (rows.length > 0 || query.isPending);

  useLayoutEffect(() => {
    if (!showing) return;
    const measure = () => {
      const box = root.current?.getBoundingClientRect();
      if (!box) return;
      const below = window.innerHeight - box.bottom - 8;
      const above = box.top - 8;
      const up = below < LIST_MAX && above > below;
      setPlace({
        left: box.left,
        width: box.width,
        ...(up
          ? { bottom: window.innerHeight - box.top + 4, maxHeight: Math.min(LIST_MAX, above) }
          : { top: box.bottom + 4, maxHeight: Math.min(LIST_MAX, below) }),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [showing]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (root.current?.contains(target) || list.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (row: Row) => {
    if (row.kind === 'tag') onPick({ tagId: row.tag.id }, { name: row.tag.name, scope: row.tag.scope, created: false });
    else onPick({ name: row.name, scope: row.scope }, { name: row.name, scope: row.scope, created: true });
    setText('');
    setTerm('');
    setActive(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => Math.min(current + 1, rows.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, -1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = active >= 0 ? rows[active] : exact ? rows.find((one) => one.kind === 'tag' && one.tag.id === exact.id) : rows[0];
      if (row) pick(row);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={root} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={placeholder}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={40}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={cx(
          'h-8 w-full min-w-0 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk',
          'placeholder:text-chalk-faint focus:border-signal focus:outline-none disabled:opacity-40',
        )}
      />
      {showing && place ? createPortal(
        <ul
          ref={list}
          id={listId}
          role="listbox"
          aria-label="Tags"
          style={place}
          className="fixed z-50 min-w-[14rem] overflow-auto rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          {rows.map((row, index) => (
            <li
              key={row.kind === 'tag' ? row.tag.id : `${row.scope}:${row.name}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => {
                // Before the input blurs, so the outside-click handler does not close us first.
                event.preventDefault();
                pick(row);
              }}
              onMouseEnter={() => setActive(index)}
              className={cx(
                'flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-caption',
                index === active ? 'bg-slate-800 text-chalk' : 'text-chalk-dim',
              )}
            >
              {row.kind === 'tag' ? (
                <>
                  <span className="truncate text-chalk">{row.tag.name}</span>
                  <span className="ml-auto flex shrink-0 items-center gap-1.5 text-micro text-chalk-faint">
                    <TagScopeLabel scope={row.tag.scope} />
                    {row.tag.videoCount > 0 ? <span className="tnum">· {row.tag.videoCount}</span> : null}
                  </span>
                </>
              ) : (
                <>
                  <PlusIcon size={12} className="shrink-0" />
                  <span className="truncate">
                    Create <span className="text-chalk">{row.name}</span>
                    {row.scope === 'team' ? ` as a team tag for ${row.teamName}` : ' as a personal tag'}
                  </span>
                  <TagScopeIcon scope={row.scope} className="ml-auto text-chalk-faint" />
                </>
              )}
            </li>
          ))}
          {rows.length === 0 && query.isPending ? <li className="px-2.5 py-1.5 text-caption text-chalk-faint">Looking</li> : null}
        </ul>,
        document.body,
      ) : null}
    </div>
  );
}
